import { CommandMan, type RecoveryWheel } from "@eisox/api/core-api";
import { ActionButton, Modal } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { PencilIcon, RecoveryWheelIcon } from "@eisox/icons";
import { getDirtyValues } from "@eisox/tools";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import { Mode } from "~/components/live-history-toggle";
import { m } from "~/paraglide/messages";

import { useUpdateAirHandlerUnit } from "../hooks/use-update-air-handler-unit";
import { useAirHandlerUnitContext } from "../providers/air-handler-unit.provider";
import * as ActuatorDialog from "./actuator-dialog";

const NAME = "RecoveryWheelDialog";

interface CommandFormValues {
  id: string;
  command: number | undefined;
  forcing: number | undefined;
}

interface RecoveryWheelDialogProps extends Modal.RootProps {
  buildingId: string;
  airHandlerUnitId: string;
  recoveryWheel: RecoveryWheel;
  children?: React.ReactNode;
}

const RecoveryWheelDialog: React.FC<RecoveryWheelDialogProps> = ({
  buildingId,
  airHandlerUnitId,
  recoveryWheel,
  open: openProp,
  // oxlint-disable-next-line typescript/unbound-method
  onOpenChange,
  defaultOpen,
  children,
}) => {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen ?? false,
  });

  const { mode } = useAirHandlerUnitContext(NAME);
  const disabled = mode === Mode.History;

  const { mutateAsync } = useUpdateAirHandlerUnit(buildingId);

  const defaultValues: CommandFormValues = {
    id: recoveryWheel.id,
    command: recoveryWheel.command,
    forcing: recoveryWheel.forcing,
  };
  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value, formApi }) => {
      if (disabled) return;
      const dirty = getDirtyValues(formApi.options.defaultValues, value, { identity: "id" });
      if (!dirty) return;
      await mutateAsync({ airHandlerUnitId, body: { recoveryWheel: dirty } });
      setOpen(false);
    },
  });

  // The form outlives the modal content, so reopening must restore the actuator's current values.
  const handleOpenChange = (next: boolean) => {
    if (next) form.reset();
    setOpen(next);
  };

  return (
    <Modal.Root open={open} onOpenChange={handleOpenChange}>
      {children && <Modal.Trigger asChild>{children}</Modal.Trigger>}
      <Modal.Content>
        <form
          onSubmit={e => {
            e.preventDefault();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <Modal.Header
              title={m.air_handler_unit_recovery_wheel()}
              subtitle={m.air_handler_unit_actuator_dialog_subtitle()}
              icon={<RecoveryWheelIcon />}
            >
              <Modal.Close asChild>
                <ActionButton variant="cancel">{m.cancel()}</ActionButton>
              </Modal.Close>
              {!disabled && <form.SubmitButton label={m.save()} icon={<PencilIcon />} />}
            </Modal.Header>
            <ActuatorDialog.Group label={m.air_handler_unit_recovery_wheel()}>
              {(recoveryWheel.command !== undefined || recoveryWheel.commandMan === CommandMan.MANUAL) && (
                <form.AppField name="command">
                  {commandField => (
                    <ActuatorDialog.Item label={m.control()}>
                      <ActuatorDialog.Command
                        commandMan={recoveryWheel.commandMan}
                        value={commandField.state.value}
                        onValueChange={value => commandField.handleChange(value)}
                        disabled={disabled}
                      />
                      <form.AppField name="forcing">
                        {forcingField => (
                          <ActuatorDialog.Forcing
                            command={commandField.state.value}
                            commandMan={recoveryWheel.commandMan}
                            commandType={recoveryWheel.commandType}
                            value={forcingField.state.value}
                            onValueChange={value => forcingField.handleChange(value)}
                            disabled={disabled}
                          />
                        )}
                      </form.AppField>
                    </ActuatorDialog.Item>
                  )}
                </form.AppField>
              )}
              <ActuatorDialog.Item label={m.state()}>
                <ActuatorDialog.ActuatorState
                  commandType={recoveryWheel.commandType}
                  state={recoveryWheel.state}
                  commandAutomate={recoveryWheel.commandAutomate}
                  commandMan={recoveryWheel.commandMan}
                  defect={!!recoveryWheel.defect}
                  missmatchDefect={!!recoveryWheel.missmatchDefect}
                />
              </ActuatorDialog.Item>
              {recoveryWheel.operationHours !== undefined && (
                <ActuatorDialog.Item label={m.air_handler_unit_actuator_operating_hours()}>
                  <ActuatorDialog.State value={`${recoveryWheel.operationHours}h`} />
                </ActuatorDialog.Item>
              )}
            </ActuatorDialog.Group>
          </form.AppForm>
        </form>
      </Modal.Content>
    </Modal.Root>
  );
};

RecoveryWheelDialog.displayName = NAME;

export { RecoveryWheelDialog };
export type { RecoveryWheelDialogProps };
